"use client";

import { m as motion } from "framer-motion";
import {
  FileText,
  MessageSquare,
  GitPullRequest,
  RotateCcw,
  CheckCircle2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useLocale } from "next-intl";
import { WeekVisual } from "./WeekVisual";

export function ProcessDemo() {
  const locale = useLocale();
  const isItalian = locale.startsWith("it");

  const steps = [
    {
      id: "step-1",
      icon: FileText,
      title: isItalian ? "Task inviato" : "Task received",
      subtitle: isItalian
        ? "Checkout e webhook Stripe in errore"
        : "Checkout and Stripe webhook failing",
      timestamp: isItalian ? "Lunedì, 09:15" : "Monday, 09:15",
      content: (
        <div className="space-y-4">
          <div className="surface-card p-4 bg-surface/50">
            <p className="font-mono text-xs text-muted-foreground mb-2">
              {isItalian ? "Brief ricevuto:" : "Brief received:"}
            </p>
            <p className="text-sm">
              {isItalian
                ? '"In produzione alcuni pagamenti falliscono: ordine creato, ma webhook non aggiorna lo stato. Serve fix rapido e verificabile."'
                : '"Some production payments fail: order is created, but webhook does not update status. Need a fast and verifiable fix."'}
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            <span>
              {isItalian
                ? "Task preso in carico con priorità alta"
                : "Task taken in charge with high priority"}
            </span>
          </div>
        </div>
      ),
    },
    {
      id: "step-2",
      icon: MessageSquare,
      title: isItalian ? "Risposta iniziale" : "Initial response",
      subtitle: isItalian
        ? "Root cause e piano tecnico"
        : "Root cause and technical plan",
      timestamp: isItalian ? "Lunedì, 16:40" : "Monday, 16:40",
      content: (
        <div className="space-y-4">
          <div className="surface-card p-4 bg-surface/50">
            <p className="font-mono text-xs text-muted-foreground mb-3">
              {isItalian ? "Analisi iniziale:" : "Initial analysis:"}
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-destructive">*</span>
                <span>
                  {isItalian
                    ? "Firma webhook non validata in modo robusto"
                    : "Webhook signature not validated robustly"}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-destructive">*</span>
                <span>
                  {isItalian
                    ? "Race condition tra conferma pagamento e update DB"
                    : "Race condition between payment confirmation and DB update"}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-destructive">*</span>
                <span>
                  {isItalian
                    ? "Mancano log strutturati per debug rapido"
                    : "Structured logs missing for fast debugging"}
                </span>
              </li>
            </ul>
          </div>
          <p className="text-sm text-muted-foreground">
            {isItalian
              ? "Stima condivisa: 1-2 giorni con fix, test e monitoraggio."
              : "Shared estimate: 1-2 days with fix, tests, and monitoring."}
          </p>
        </div>
      ),
    },
    {
      id: "step-3",
      icon: GitPullRequest,
      title: isItalian ? "Consegna" : "Delivery",
      subtitle: isItalian
        ? "Fix backend + hardening"
        : "Backend fix + hardening",
      timestamp: isItalian ? "Giovedì, 14:00" : "Thursday, 14:00",
      content: (
        <div className="space-y-4">
          <div className="surface-card p-4 bg-surface/50 font-mono text-xs">
            <p className="text-muted-foreground mb-2">
              {isItalian
                ? "File principali aggiornati:"
                : "Main files updated:"}
            </p>
            <div className="space-y-1 text-primary">
              <p>+ src/api/webhooks/stripe.ts</p>
              <p>+ src/services/payment/checkout.ts</p>
              <p>+ src/services/orders/order-status.ts</p>
              <p>+ src/tests/webhooks/stripe-webhook.spec.ts</p>
            </div>
          </div>
          <div className="surface-card p-4 bg-surface/50 font-mono text-xs">
            <p className="text-muted-foreground mb-2">
              {isItalian ? "Verifica:" : "Verification:"}
            </p>
            <p className="text-green-500">
              {isItalian
                ? "Webhook verificato con signature valida"
                : "Webhook verified with valid signature"}
            </p>
            <p className="text-green-500">
              {isItalian
                ? "Aggiornamento ordine idempotente"
                : "Idempotent order update"}
            </p>
            <p className="text-green-500">
              {isItalian
                ? "Test automatici e build OK"
                : "Automated tests and build OK"}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: "step-4",
      icon: RotateCcw,
      title: isItalian ? "Revisione" : "Review",
      subtitle: isItalian ? "QA con dati reali" : "QA with real data",
      timestamp: isItalian ? "Venerdì, 10:20" : "Friday, 10:20",
      content: (
        <div className="space-y-4">
          <div className="surface-card p-4 bg-surface/50">
            <p className="font-mono text-xs text-muted-foreground mb-3">
              {isItalian ? "Feedback:" : "Feedback:"}
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <span>
                  {isItalian
                    ? "Confermata stabilità checkout su pagamenti reali"
                    : "Checkout stability confirmed on real payments"}
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                <span>
                  {isItalian
                    ? "Aggiunti log e alert per eventuali retry"
                    : "Added logs and alerts for potential retries"}
                </span>
              </li>
            </ul>
          </div>
          <p className="text-sm text-muted-foreground">
            {isItalian
              ? "Micro-refactor finale e rilascio confermato."
              : "Final micro-refactor and confirmed release."}
          </p>
        </div>
      ),
    },
    {
      id: "step-5",
      icon: CheckCircle2,
      title: isItalian ? "Conclusione" : "Completion",
      subtitle: isItalian
        ? "Task chiuso e tracciato"
        : "Task closed and tracked",
      timestamp: isItalian ? "Venerdì, 17:10" : "Friday, 17:10",
      content: (
        <div className="space-y-4">
          <div className="surface-card p-4 bg-surface/50">
            <p className="font-mono text-xs text-muted-foreground mb-3">
              {isItalian ? "Recap finale:" : "Final recap:"}
            </p>
            <ul className="space-y-2 text-sm">
              <li>
                *{" "}
                {isItalian
                  ? "Issue pagamento risolta"
                  : "Payment issue resolved"}
              </li>
              <li>
                *{" "}
                {isItalian
                  ? "Tempo medio fix: 2 giorni"
                  : "Average fix time: 2 days"}
              </li>
              <li>
                *{" "}
                {isItalian
                  ? "Stack coperto: backend, integrazione, test"
                  : "Covered stack: backend, integration, tests"}
              </li>
              <li>
                *{" "}
                {isItalian
                  ? "Prossimo task pronto in coda"
                  : "Next task ready in queue"}
              </li>
            </ul>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section className="py-20 md:py-28 lg:py-28 bg-surface/30">
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-left mb-12"
        >
          <span className="font-mono text-label text-primary mb-4 block">
            {isItalian ? "Processo operativo" : "Operational process"}
          </span>
          <h2 className="font-display text-h2 font-bold mb-4">
            {isItalian ? "Una settimana tipo" : "A typical week"}
          </h2>
          <p className="text-body-lg text-muted-foreground max-w-2xl">
            {isItalian
              ? "Ecco come viene gestito un task reale, dalla richiesta alla chiusura."
              : "Here is how a real task is handled, from request to completion."}
          </p>
        </motion.div>

        <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_24rem] lg:items-center lg:gap-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="max-w-2xl"
          >
            <Accordion type="single" collapsible className="space-y-3">
            {steps.map((step) => (
              <AccordionItem
                key={step.id}
                value={step.id}
                className="surface-card border-none overflow-hidden"
              >
                <AccordionTrigger className="hover:no-underline px-6 py-5 [&[data-state=open]>div>.step-icon]:bg-primary [&[data-state=open]>div>.step-icon]:text-primary-foreground">
                  <div className="flex items-center gap-4 text-left w-full pr-3 sm:pr-4">
                    <div className="step-icon w-10 h-10 rounded-lg bg-surface text-primary flex items-center justify-center transition-colors duration-200 shrink-0">
                      <step.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-display font-semibold">
                          {step.title}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {step.subtitle}
                      </p>
                    </div>
                    <span className="font-mono text-xs text-muted-foreground hidden sm:block">
                      {step.timestamp}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6 pt-0">
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="pl-14"
                  >
                    {step.content}
                  </motion.div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="hidden lg:flex lg:justify-center"
          >
            <WeekVisual isItalian={isItalian} />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
